import { ChangeDetectionStrategy, Component, computed, effect, Inject, inject, signal, untracked, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { EnableEscCloseDirective, UiButtonComponent, UiModels, UiNoDataComponent, UiSearchComponent } from '@ui';
import { DatabaseTabs, DatabaseTabsComponent } from '../../../../components/database-tabs/database-tabs.component';
import { Store } from '@ngrx/store';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { VehiclesTableComponent } from '../../components/vehicles-table/vehicles-table.component';
import { UnsavedVehiclesTableComponent } from '../../components/unsaved-vehicles-table/unsaved-vehicles-table.component';
import { FormsModule } from '@angular/forms';
import { UiPreloaderComponent } from '../../../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { SharedModels } from '@share-models';
import { features } from '@consts/text.const';
import { Actions, ofType } from '@ngrx/effects';
import { filter, take, takeUntil } from 'rxjs';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { VehiclesSelectors } from '@states/vehicles/vehicles.selector-types';
import { SelectionChangeEvent } from '@ui/lib/ui-kit/ui-dataset-table';
import { VehicleModels } from '@models/vehicle.model';
import { SaveVehiclesDialogComponent } from '../save-vehicles-dialog/save-vehicles-dialog.component';
import { UiVehicleDialogComponent } from '../ui-vehicle-dialog/ui-vehicle-dialog.component';

@Component({
  selector: 'app-add-vehicle-to-group-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    MatTooltipModule,
    UiButtonComponent,
    DatabaseTabsComponent,
    UiSearchComponent,
    UiPreloaderComponent,
    UiNoDataComponent,
    VehiclesTableComponent,
    UnsavedVehiclesTableComponent,
    FormsModule,
  ],
  templateUrl: './add-vehicle-to-group-dialog.component.html',
  styleUrl: './add-vehicle-to-group-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class AddVehicleToGroupDialogComponent {
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(MatDialog);

  protected readonly tabs = VehicleGroupsModel.AddVehicleToGroupDialogTab;
  protected readonly currentTab = signal<VehicleGroupsModel.AddVehicleToGroupDialogTab>(
    VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles,
  );
  protected readonly searchQuery = signal<string>('');
  protected readonly viewState = SharedModels.ViewState;
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly vehicleNoData = features.vehicles;

  private readonly savedVehicles = this.store$.selectSignal(VehiclesSelectors.selectSavedVehicles);
  private readonly unsavedVehicles = this.store$.selectSignal(VehiclesSelectors.selectUnsavedVehicles);
  private readonly groupMembers = this.store$.selectSignal(vehicleGroupsFeature.selectAllVehicleGroupMembers);
  private readonly existingVehiclesTable = viewChild<VehiclesTableComponent>('existingVehiclesTable');

  private readonly isSavedVehiclesLoading = this.store$.selectSignal(VehiclesSelectors.selectIsLoadingSavedVehicles);
  private readonly isUnsavedVehiclesLoading = this.store$.selectSignal(VehiclesSelectors.selectIsLoadingUnsavedVehicles);

  protected readonly isVehiclesLoading = computed(() =>
    this.currentTab() === VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles
      ? this.isSavedVehiclesLoading()
      : this.isUnsavedVehiclesLoading(),
  );
  protected readonly isAddingVehiclesToGroups = this.store$.selectSignal(
    vehicleGroupsFeature.selectIsAddingVehiclesToGroups,
  );

  protected readonly existingVehiclesCount = computed(() => this.savedVehicles().length);
  protected readonly unsavedVehiclesCount = computed(() => this.unsavedVehicles().length);

  protected readonly dialogTabs: DatabaseTabs<VehicleGroupsModel.AddVehicleToGroupDialogTab>[] = [
    {
      value: VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles,
      labelKey: 'VEHICLES.BUTTONS.EXISTING_VEHICLES',
      iconName: 'ui-icon-object-vehicle',
      count: this.existingVehiclesCount,
    },
    {
      value: VehicleGroupsModel.AddVehicleToGroupDialogTab.UnsavedVehicles,
      labelKey: 'VEHICLES.BUTTONS.UNSAVED_VEHICLES',
      iconName: 'ui-icon-unsaved-vehicles',
      count: this.unsavedVehiclesCount,
    },
  ];

  protected readonly searchPlaceholderKey = computed(() =>
    this.currentTab() === this.tabs.ExistingVehicles
      ? 'VEHICLES.PLACEHOLDERS.SEARCH_EXISTING'
      : 'VEHICLES.PLACEHOLDERS.SEARCH_UNSAVED',
  );

  protected readonly vehicleGroupMemberIdsSet = computed(
    () => new Set(this.data.vehicleGroupMemberIds),
  );

  protected readonly filteredSavedVehicles = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const memberIds = this.vehicleGroupMemberIdsSet();
    const filtered = this.savedVehicles().filter((vehicle) => {
      if (vehicle.id == null) {
        return false;
      }
      if (!search) {
        return true;
      }
      return (
        vehicle.plate?.toLowerCase().includes(search) ||
        vehicle.owner?.toLowerCase().includes(search) ||
        vehicle.make?.toLowerCase().includes(search) ||
        vehicle.color?.toLowerCase().includes(search)
      );
    });

    const justCreatedPlate = this.justCreatedPlate();

    return [...filtered].sort((a, b) => {
      const aDisabled = a.id != null && memberIds.has(a.id);
      const bDisabled = b.id != null && memberIds.has(b.id);
      if (aDisabled !== bDisabled) {
        return Number(aDisabled) - Number(bDisabled);
      }
      if (justCreatedPlate) {
        if (a.plate === justCreatedPlate) return -1;
        if (b.plate === justCreatedPlate) return 1;
      }
      return 0;
    });
  });
  protected readonly existingVehiclesInGroupPlates = computed(() =>
    this.filteredSavedVehicles()
      .filter((vehicle) => this.isSavedVehicleRowDisabled(vehicle))
      .map((vehicle) => vehicle.plate),
  );

  protected readonly filteredUnsavedVehicles = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const memberPlates = new Set(
      this.groupMembers()
        .map((vehicle) => vehicle.plate?.trim().toLowerCase())
        .filter((plate): plate is string => !!plate),
    );
    return this.unsavedVehicles().filter((vehicle) => {
      const plate = vehicle.plate?.trim().toLowerCase() ?? '';
      if (memberPlates.has(plate)) {
        return false;
      }
      if (!search) {
        return true;
      }
      return plate.includes(search);
    });
  });

  protected readonly savedVehiclesViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isVehiclesLoading();
    const hasSearch = this.searchQuery().trim().length > 0;
    const filteredVehicles = this.filteredSavedVehicles();

    if (isLoading) {
      return SharedModels.ViewState.INITIAL_EMPTY;
    }
    if (filteredVehicles.length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    if (hasSearch) {
      return SharedModels.ViewState.NO_RESULTS;
    }
    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly unsavedVehiclesViewState = computed<SharedModels.ViewState>(() => {
    const isLoading = this.isVehiclesLoading();
    const hasSearch = this.searchQuery().trim().length > 0;
    const filteredVehicles = this.filteredUnsavedVehicles();

    if (isLoading) {
      return SharedModels.ViewState.INITIAL_EMPTY;
    }
    if (filteredVehicles.length > 0) {
      return SharedModels.ViewState.HAS_CONTENT;
    }
    if (hasSearch) {
      return SharedModels.ViewState.NO_RESULTS;
    }
    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly selectedSavedVehicleIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedUnsavedPlates = signal<Set<string>>(new Set<string>());
  /** Plate of a just-created vehicle awaiting force-selection once its table row renders. */
  private readonly pendingPreselectPlate = signal<string | null>(null);
  /** Plate of the most recently created vehicle in this dialog session; pins it to the top of the list. */
  protected readonly justCreatedPlate = signal<string | null>(null);

  protected readonly selectedItemsCount = computed(
    () => this.selectedSavedVehicleIds().size + this.selectedUnsavedPlates().size,
  );

  protected readonly addSelectedDisabled = computed(
    () => this.selectedItemsCount() === 0 || this.isAddingVehiclesToGroups(),
  );

  protected readonly isSavedVehicleRowDisabled = (vehicle: VehicleModels.Vehicle): boolean =>
    vehicle.id != null && this.vehicleGroupMemberIdsSet().has(vehicle.id);

  public constructor(
    private readonly dialogRef: MatDialogRef<AddVehicleToGroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: VehicleGroupsModel.AddVehicleToGroupDialogData,
  ) {
    effect(
      () => {
        const table = this.existingVehiclesTable();
        const preselectedPlates = this.existingVehiclesInGroupPlates();
        const pendingPlate = this.pendingPreselectPlate();

        if (!table) {
          return;
        }

        // Tracked so the effect retries once the newly created vehicle's row
        // actually propagates into the table's data input.
        const rows = table.vehicles();

        untracked(() => {
          const desired = new Set<string>(table.selectedPlatesRows());
          preselectedPlates.forEach((plate) => desired.add(plate));

          // Only add the just-created vehicle once its row is actually present,
          // else the table's own cleanup prunes the unknown id.
          if (pendingPlate && rows.some((vehicle) => vehicle.plate === pendingPlate)) {
            desired.add(pendingPlate);
            this.pendingPreselectPlate.set(null);
          }

          table.applyForcedSelection([...desired]);
        });
      },
      { allowSignalWrites: true },
    );
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  /** Opens the production "add vehicle" dialog; the created vehicle lands preselected in the Existing tab. */
  protected createVehicle(): void {
    const dialogRef = this.dialog.open(UiVehicleDialogComponent, {
      width: '540px',
      disableClose: true,
    });

    this.actions$
      .pipe(ofType(VehiclesActions.addVehicleSuccess), takeUntil(dialogRef.afterClosed()), take(1))
      .subscribe(({ vehicle }) => {
        this.currentTab.set(VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles);
        this.searchQuery.set('');
        this.justCreatedPlate.set(vehicle.plate);
        this.pendingPreselectPlate.set(vehicle.plate);
      });
  }

  protected changeActiveTab(tab: VehicleGroupsModel.AddVehicleToGroupDialogTab): void {
    this.currentTab.set(tab);
    this.searchQuery.set('');
  }

  protected onSavedVehiclesSelectionChange(event: SelectionChangeEvent<VehicleModels.Vehicle>): void {
    const nextSelectedVehicleIds = new Set<number>();
    for (const vehicle of event.selectedRows) {
      if (vehicle.id != null && !this.isSavedVehicleRowDisabled(vehicle)) {
        nextSelectedVehicleIds.add(vehicle.id);
      }
    }
    this.selectedSavedVehicleIds.set(nextSelectedVehicleIds);
  }

  protected onUnsavedVehiclesSelectionChange(event: SelectionChangeEvent<VehicleModels.Vehicle>): void {
    this.selectedUnsavedPlates.set(new Set(event.selectedIds.map((id) => String(id))));
  }

  protected onAddSelected(): void {
    if (this.addSelectedDisabled()) {
      return;
    }

    const savedVehicleIds = Array.from(this.selectedSavedVehicleIds());
    const selectedUnsavedVehicles = this.getSelectedUnsavedVehicles();

    if (selectedUnsavedVehicles.length) {
      this.dialog
        .open<SaveVehiclesDialogComponent, VehicleModels.SaveVehiclesDialogData, boolean>(
          SaveVehiclesDialogComponent,
          {
            width: '820px',
            panelClass: 'modal-no-padding',
            maxHeight: '80vh',
            disableClose: false,
            data: {
              vehicles: selectedUnsavedVehicles,
              groupIds: [this.data.groupId],
              savedVehicleIds,
            },
          },
        )
        .afterClosed()
        .pipe(
          filter(Boolean),
          take(1),
        )
        .subscribe(() => this.dialogRef.close());
      return;
    }

    this.actions$
      .pipe(
        ofType(VehicleGroupsActions.addVehiclesToGroupsSuccess),
        filter(({ groupIds }) => groupIds.includes(this.data.groupId)),
        take(1),
      )
      .subscribe(() => {
        this.dialogRef.close();
      });

    this.store$.dispatch(
      VehicleGroupsActions.startAddingVehiclesToGroups({
        groupIds: [this.data.groupId],
        savedVehicleIds,
        unsavedVehicleDtos: [],
      }),
    );
  }

  private getSelectedUnsavedVehicles(): VehicleModels.Vehicle[] {
    const selectedPlates = this.selectedUnsavedPlates();

    return this.unsavedVehicles().filter((vehicle) => selectedPlates.has(vehicle.plate));
  }
}
